import { useMemo, useState } from 'react'
import type { Control } from 'react-hook-form'
import { Separator } from '@/ui/separator'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { MaterialIcon } from '@/ui/material-icon'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/ui/form'
import { cn } from '@/shared/lib/utils'
import type { RolePermissionModule } from '../../types'
import type { FeatureSelection } from '../../utils/permissionTree'
import type { RoleFormValues } from '../../schemas/role.schema'
import styles from './CreateRole.module.scss'

interface CreateRoleProps {
  title: string
  /** RHF control for the `title` / `description` text fields. */
  control: Control<RoleFormValues>
  /** Permission tree — live `/roles/modules` graph merged with catalog names. */
  tree: RolePermissionModule[]
  treeLoading: boolean
  /** Module ids currently "assigned" (UI scoping for the feature tree below). */
  selectedModuleIds: number[]
  /** `featureId` → granted. */
  featureSelection: FeatureSelection
  onModuleAssignChange: (moduleId: number, assigned: boolean) => void
  onSelectAllModules: (assigned: boolean) => void
  onFeatureToggle: (featureId: number, checked: boolean) => void
  onModuleFeaturesToggle: (moduleId: number, checked: boolean) => void
}

export default function CreateRole({
  title,
  control,
  tree,
  treeLoading,
  selectedModuleIds,
  featureSelection,
  onModuleAssignChange,
  onSelectAllModules,
  onFeatureToggle,
  onModuleFeaturesToggle,
}: CreateRoleProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [collapsedModules, setCollapsedModules] = useState<Record<number, boolean>>({})

  const filteredModules = useMemo(
    () =>
      tree.filter((module) =>
        module.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
      ),
    [tree, searchTerm]
  )

  const selectedSet = useMemo(
    () => new Set(selectedModuleIds),
    [selectedModuleIds]
  )

  const assignedModules = useMemo(
    () => tree.filter((module) => selectedSet.has(module.moduleId)),
    [tree, selectedSet]
  )

  const allVisibleAssigned =
    filteredModules.length > 0 &&
    filteredModules.every((module) => selectedSet.has(module.moduleId))

  const allCollapsed =
    assignedModules.length > 0 &&
    assignedModules.every((module) => collapsedModules[module.moduleId])

  const isModuleFullySelected = (module: RolePermissionModule) =>
    module.features.length > 0 &&
    module.features.every((feature) => featureSelection[feature.featureId])

  const isModulePartiallySelected = (module: RolePermissionModule) =>
    module.features.some((feature) => featureSelection[feature.featureId]) &&
    !isModuleFullySelected(module)

  const toggleCollapse = (moduleId: number) =>
    setCollapsedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }))

  const handleCollapseAll = () => {
    const next: Record<number, boolean> = {}
    assignedModules.forEach((module) => {
      next[module.moduleId] = !allCollapsed
    })
    setCollapsedModules(next)
  }

  return (
    <div className={styles.createRoleWrapper}>
      {/* FORM SECTION */}
      <div className={styles.cardBody}>
        <Separator />
        <div className={styles.cardHeader}>
          <h1 className={styles.title}>{title}</h1>
        </div>
        <Separator />

        <FormField
          control={control}
          name="title"
          render={({ field }) => (
            <FormItem className={styles.formField}>
              <FormLabel className={styles.fieldLabel}>Role Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  placeholder="Enter role name"
                  className={styles.fieldInput}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem className={styles.formField}>
              <FormLabel className={styles.fieldLabel}>
                Role Description
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter role description"
                  rows={4}
                  className={cn(styles.textfeild, 'bg-[#F9FAFB]')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* MODULE ASSIGNMENT SECTION */}
      <div className={styles.assignModulesCard}>
        <Separator />
        <div className={styles.header}>
          <div className={styles.cardHeader}>
            <h2 className={styles.title}>Assign Modules to this role</h2>
          </div>
          <div className={styles.searchBox}>
            <MaterialIcon name="search" size={16} />
            <Input
              type="text"
              placeholder="Search Module"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className={cn(
                styles.searchInput,
                'focus-visible:ring-0 focus-visible:ring-offset-0'
              )}
            />
          </div>
        </div>
        <Separator />

        <div className={styles.moduleList}>
          <label className={styles.selectAllLabel}>
            <input
              type="checkbox"
              checked={allVisibleAssigned}
              onChange={(event) => onSelectAllModules(event.target.checked)}
            />
            Select All
          </label>

          {treeLoading ? (
            <div className={styles.noModules}>Loading modules...</div>
          ) : filteredModules.length === 0 ? (
            <div className={styles.noModules}>No modules found</div>
          ) : (
            filteredModules.map((module) => (
              <label className={styles.moduleItem} key={module.moduleId}>
                <input
                  className={styles.checkbox}
                  type="checkbox"
                  checked={selectedSet.has(module.moduleId)}
                  onChange={(event) =>
                    onModuleAssignChange(module.moduleId, event.target.checked)
                  }
                />
                <span>{module.name}</span>
              </label>
            ))
          )}
        </div>
      </div>

      {/* FEATURE PERMISSIONS SECTION */}
      <div className={styles.selectedModulesPermissions}>
        <Separator />
        <div className={styles.cardHeader}>
          <h2 className={styles.title}>Select Module Feature Permissions</h2>
          {assignedModules.length > 0 && (
            <button
              type="button"
              className={styles.collapseAllBtn}
              onClick={handleCollapseAll}
            >
              <MaterialIcon
                name={allCollapsed ? 'expand_more' : 'expand_less'}
                size={16}
              />
              {allCollapsed ? 'Expand All' : 'Collapse All'}
            </button>
          )}
        </div>
        <Separator />

        <p className={styles.permissionsHint}>
          Assign a module above, then grant the individual features within it.
          Some features depend on others — enabling or disabling one updates its
          dependencies automatically.
        </p>

        {treeLoading ? (
          <div className={styles.noModules}>Loading module features...</div>
        ) : assignedModules.length === 0 ? (
          <div className={styles.noModules}>
            Assign modules above to grant their features
          </div>
        ) : (
          <div className={styles.permissionsModulesList}>
            {assignedModules.map((module) => {
              const open = !collapsedModules[module.moduleId]
              return (
                <div key={module.moduleId} className={styles.permissionsModule}>
                  <button
                    type="button"
                    className={styles.permissionsModuleHeader}
                    onClick={() => toggleCollapse(module.moduleId)}
                  >
                    <div className={styles.permissionsModuleLeft}>
                      <input
                        type="checkbox"
                        checked={isModuleFullySelected(module)}
                        ref={(el) => {
                          if (el)
                            el.indeterminate = isModulePartiallySelected(module)
                        }}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) =>
                          onModuleFeaturesToggle(
                            module.moduleId,
                            event.target.checked
                          )
                        }
                      />
                      <span className={styles.permissionsModuleName}>
                        {module.name}
                      </span>
                    </div>
                    <MaterialIcon
                      name={open ? 'expand_more' : 'chevron_right'}
                      size={18}
                      className={styles.permissionsChevron}
                    />
                  </button>

                  {open && (
                    <div className={styles.permissionsModuleBody}>
                      {module.features.map((feature) => (
                        <label
                          key={feature.featureId}
                          className={styles.permissionItem}
                        >
                          <input
                            type="checkbox"
                            checked={!!featureSelection[feature.featureId]}
                            onChange={(event) =>
                              onFeatureToggle(
                                feature.featureId,
                                event.target.checked
                              )
                            }
                          />
                          <div className={styles.permissionText}>
                            <div className={styles.permissionName}>
                              {feature.name}
                            </div>
                            {feature.description && (
                              <div className={styles.permissionDesc}>
                                {feature.description}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
